import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import * as QRCode from 'qrcode';
import { errorCopy } from '../common/errorCopy';
import { LanguageListService } from '../service/languagelist.service';
import { LanguageItem } from '../model/languageitem';

import { MeetingItem } from '../model/meetingitem';
import { ReturnObject } from '../model/returnobject';
import { MeetingService } from '../service/meeting.service';

@Component({
    selector: 'app-meeting',
    templateUrl: './meeting.component.html',
    styleUrls: ['./meeting.component.css'],
    standalone: false
})
export class MeetingComponent implements OnInit, OnDestroy {
    static snackBarErrMsg: string = '';
    BASE_URL: string = window.location.origin + '/#/session/';
    displayedColumns: string[] = ['name', 'startMeetingButton', 'key', 'moderatorKey'];
    myMeetings: MeetingItem[] = []

    meetingName: string = "";
    generatedMeetingKey: string = "";
    generatedModeratorKey: string = "";

    languageSubscr: Subscription = new Subscription;
    languageItemList: LanguageItem[] = [];
    showSnackBar: boolean = true;
    showTooltipMobile: boolean = false;

    errMessage: string = '';
    toastVariant: string = '';
    toastHeader: string = '';
    toastSubHeader: string = ''

    editDialogOpen = false;
    editName = "";
    selectedMeeting: MeetingItem | null = null;

    mobilePopoverItem: MeetingItem | null = null;

    selectedTab: 'active' | 'archive' = 'active';

    qrDialogOpen = false;
    qrValue = '';
    qrDataUrl: string | null = null;

    constructor(
        private meetingService: MeetingService,
        private languageListService: LanguageListService,
        private router: Router,
        private errCopy: errorCopy) { }

    get dataSource() {
        return this.myMeetings.filter(m =>
            this.selectedTab === 'active' ? !m.archived : m.archived
        );
    }

    onTabChange(event: any) {
        const index = event.detail?.selectedTabIndex ?? 0;
        this.selectedTab = index === 0 ? 'active' : 'archive';
    }

    openMobilePopover(item: MeetingItem) {
        this.mobilePopoverItem = item;
    }

    ngOnDestroy(): void {
        this.languageSubscr.unsubscribe();
    }

    ngOnInit(): void {
        this.languageSubscr = this.languageListService.languageitemlist$.subscribe(
            words => {
                this.languageItemList = words;
            });

        this.languageListService.fetchData();
        this.getMeetingsForUser();
    }

    getLanguageItem(key: string) {
        if (this.languageItemList.length != 0)
            return this.languageListService.getLanguageItem(key).textString;
        return "...";
    }

    getMeetingsForUser() {
        this.meetingService.getMeetingsForUser()
            .subscribe({
                next: (result: ReturnObject<MeetingItem[]>) => {
                    if (result.isFailure) {
                        this.openSnackBarErr(result.failureMessage);
                        return;
                    }
                    this.myMeetings = result.value;
                },
                error: (err) => {
                    this.openSnackBarErr(err.message);
                }
            });
    }

    createMeeting() {
        this.meetingService.createMeeting(this.meetingName)
            .subscribe({
                next: (result: ReturnObject<MeetingItem>) => {
                    if (result.isFailure) {
                        this.openSnackBarErr(result.failureMessage);
                        return;
                    }
                    this.myMeetings.push(result.value);
                    window.location.reload()
                },
                error: (err) => {
                    this.openSnackBarErr(err.message);
                }
            });
    }

    isCreateDisabled(): boolean {
        if (this.meetingName.length > 0) {
            return false;
        } else {
            return true;
        }
    }

    startMeeting(meetingKey: string, moderatorKey: string): void {
        var link = "/session/" + meetingKey + "/" + moderatorKey;
        this.router.navigate([link]);
    }

    createMeetingLink(meetingKey: string): string {
        return this.BASE_URL + meetingKey;
    }

    createModeratorLink(meetingKey: string, moderatorKey: string): string {
        return this.BASE_URL + meetingKey + '/' + moderatorKey;
    }

    onCopied(type: 'participant' | 'moderator') {
        if (this.selectedTab === 'archive') return;
        this.toastVariant = 'success';
        this.errMessage = type === 'participant' ? 'Participant link copied' : 'Moderator link copied';
        setTimeout(() => (this.errMessage = ''), 2500);
    }

    openSnackBarErr(message: string): void {
        this.toastVariant = 'error';
        this.toastHeader = 'Error';
        this.errMessage = 'An unexpected error occurred. The error was: ' + message;
        setTimeout(() => {
                this.errMessage = '';
            }, 20000
        );
    }

    saveEditedMeeting() {
        if (!this.selectedMeeting) return;

        const updatedMeeting: MeetingItem = {
            ...this.selectedMeeting,
            name: this.editName.trim()
        };

        this.meetingService.updateMeeting(updatedMeeting).subscribe({
            next: (result: ReturnObject<MeetingItem>) => {
                if (result.isFailure) {
                    this.openSnackBarErr(result.failureMessage);
                    return;
                }
                // Update UI list without reload
                const index = this.myMeetings.findIndex(m => m.id === updatedMeeting.id);
                if (index !== -1) {
                    this.myMeetings[index] = result.value;
                }
                this.editDialogOpen = false;
                this.selectedMeeting = null;
                this.editName = "";
                this.toastVariant = 'success';
                this.toastHeader = 'Updated';
                this.errMessage = 'Meeting Name - '+ updatedMeeting.name +' updated successfully.';
                setTimeout(() => this.errMessage = '', 5000);
            },
            error: (err) => {
                this.openSnackBarErr(err.message);
            }
        });
    }

    archiveMeeting(item: any) {
        this.meetingService.archiveMeeting(item.id)
            .subscribe({
                next: (result: ReturnObject<MeetingItem>) => {
                    if (result.isFailure) {
                        this.openSnackBarErr(result.failureMessage);
                        return;
                    }
                    const updated = result.value;

                    // Find the existing meeting in the list
                    const index = this.myMeetings.findIndex(m => m.id === updated.id);

                    if (index !== -1) {
                        // Replace the item with the updated one from backend
                        this.myMeetings[index] = updated;
                    }

                    this.toastVariant = 'success';
                    this.toastHeader = updated.archived ? 'Archived' : 'Unarchived';
                    this.errMessage = updated.archived
                        ? 'Meeting Name - '+ updated.name +' - Archived successfully.'
                        : 'Meeting Name - '+ updated.name +' - Unarchived successfully.';

                    setTimeout(() => this.errMessage = '', 5000);
                },
                error: (err) => {
                    this.openSnackBarErr(err.message);
                }
            });
    }

    editMeeting(item: MeetingItem) {
        this.selectedMeeting = {...item};
        this.editName = item.name;
        this.editDialogOpen = true;
    }

    closeEditModal() {
        this.editDialogOpen = false;
        this.selectedMeeting = null;
        this.editName = "";
    }

    async showQrCode(item: any) {
        this.qrValue = this.createMeetingLink(item.key);
        this.qrDialogOpen = true;
        this.qrDataUrl = await QRCode.toDataURL(this.qrValue, {
            width: 360,
            margin: 2
        });
    }

    closeQrModal() {
        this.qrDialogOpen = false;
        this.qrValue = '';
        this.qrDataUrl = null;
    }

    downloadQr() {
        if (!this.qrDataUrl) return;

        const a = document.createElement('a');
        a.href = this.qrDataUrl;
        a.download = 'Meeting-QR.png';
        a.click();
    }

    async copyQrImage() {
        if (!this.qrDataUrl) return;
        try {
            const res = await fetch(this.qrDataUrl);
            const blob = await res.blob();
            await navigator.clipboard.write([
                new ClipboardItem({ [blob.type]: blob })
            ]);
            this.toastVariant = 'success';
            this.toastHeader = this.getLanguageItem('titlecopy') || 'Copied';
            this.errMessage = 'QR code copied';
            setTimeout(() => (this.errMessage = ''), 2500);
        } catch (e) {
            await navigator.clipboard.writeText(this.qrValue);
            this.toastVariant = 'success';
            this.toastHeader = this.getLanguageItem('titlecopy') || 'Copied';
            this.errMessage = 'QR image not supported — link copied instead';
            setTimeout(() => (this.errMessage = ''), 2500);
        }
    }
}
